import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faDoorOpen,
  faHammer,
  faPlane,
  faHotel,
  faUmbrellaBeach,
  faGraduationCap,
  faTimes,
  faDeleteLeft, faLocationDot, faStar,
  faHeart,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faCalendar,
  faArrowDownAZ,
  faTags,
  faTrain
} from '@fortawesome/free-solid-svg-icons'
import {db, functions} from '../../firebase/FirebaseProvider'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Location, User, Visit, NearestPub } from "../types/Types";
import { LocationTagOption, ManagedTag } from "../types/LocationTags";
import LocationRow from "./LocationRow";
import { httpsCallable } from "@firebase/functions";
import { Form, Button, Card, Col, InputGroup, ListGroup, Row } from "react-bootstrap";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import AuthCheck from "../auth/AuthCheck";
import PageTracking from "../auth/PageTracking";
import CrosshairsTag from "../tag/CrosshairsTag";
import { formatKilometres } from "../util/Util";
import LocationTag from "../tag/LocationTag";
import LoadingTag from "../tag/LoadingTag";
import {useNavigate, useSearchParams} from "react-router-dom";
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../shared/PaginationControls";
import NearestPubMap from "./NearestPubMap";

interface Props {
  locations: Array<Location>;
}

type NearestPubSearchType = 'all' | 'notVisited' | 'notCheckedIn';

interface Coordinates {
  lat: number;
  lng: number;
}

const getValidDate = (dateString: string | undefined): Date | undefined => {
  if (!dateString) return undefined;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return undefined;

  return date;
};

const getTimeOpenInDays = (location: Location): number => {
  const openingDate = getValidDate(location.openingDate);
  if (!openingDate) return 1;
  if (location.pubIsClosed && !getValidDate(location.closingDate)) return 1;

  const endDate = getValidDate(location.closingDate) || new Date();
  if (endDate < openingDate) return 1;

  const dayCount = Math.floor((endDate.getTime() - openingDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(dayCount, 1);
};

const Locations: React.FC<Props> = ({ locations: propLocations }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasInitializedRef = useRef(false);
  const prevSearchParamRef = useRef<string | null>(null);
  
  // State declarations
  const [loading, setLoading] = useState<boolean>(true);
  const [filteredLocations, setFilteredLocations] = useState<Array<Location>>([]);
  const [locationsMap, setLocationsMap] = useState<Map<string, Location>>(new Map());
  const [currentUser, setCurrentUser] = useState<User>({} as User);
  const [visitsMap, setVisitsMap] = useState<Map<string, Visit>>(new Map());
  const [locationModifiedDate, setLocationModifiedDate] = useState<Date>(new Date());
  const [visitModifiedDate, setVisitModifiedDate] = useState<Date>(new Date());
  const [sortType, setSortType] = useState<string>('a-z');
  const [queryString, setQueryString] = useState<string>('');
  const [filter, setFilter] = useState<string>('');
  const [locationCount, setLocationCount] = useState<number>(0);
  const [openCount, setOpenCount] = useState<number>(0);
  const { pageStart, pageEnd, pageLeft, pageRight, resetPagination, getPaginatedItems } = usePagination<Location>({ pageSize: 50 });
  const [findingNearest, setFindingNearest] = useState<boolean>(false);
  const [nearestPubExpanded, setNearestPubExpanded] = useState<boolean>(false);
  const [nearestPubSearchType, setNearestPubSearchType] = useState<NearestPubSearchType>('all');
  const [selectedNearestPubSearchType, setSelectedNearestPubSearchType] = useState<NearestPubSearchType | undefined>(undefined);
  const [nearestPubId, setNearestPubId] = useState<string>('');
  const [nearestPubName, setNearestPubName] = useState<string>('');
  const [nearestPubFeedback, setNearestPubFeedback] = useState<string>('');
  const [nearestPubMapCoordinates, setNearestPubMapCoordinates] = useState<Coordinates | undefined>(undefined);
  const [nearestPubMapLocation, setNearestPubMapLocation] = useState<Location | undefined>(undefined);
  const [validLocationTagNames, setValidLocationTagNames] = useState<Set<string>>(new Set());
  const [locationTagOptions, setLocationTagOptions] = useState<Array<LocationTagOption>>([]);
  const [locationTagsExpanded, setLocationTagsExpanded] = useState<boolean>(false);
  const [selectedLocationTagId, setSelectedLocationTagId] = useState<string>('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        navigate('/');
        return;
      }
      const userCollection = query(collection(db, 'users'), where('authId', '==', authUser.uid));
      const userCollectionSnap = await getDocs(userCollection);
      userCollectionSnap.forEach((userDoc) => {
        let userToSet: User = userDoc.data() as User;
        userToSet.id = userDoc.id;
        setCurrentUser(userToSet);
      });
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    (async () => {
      const locationTagsQuery = query(
          collection(db, 'tags'),
          where('type', '==', 'LOCATION')
      );
      const locationTagsSnapshot = await getDocs(locationTagsQuery);
      const loadedLocationTagNames = new Set<string>();
      const loadedLocationTagOptions: Array<LocationTagOption> = [];

      locationTagsSnapshot.forEach((tagDoc) => {
        const tag = tagDoc.data() as ManagedTag;
        if(tag.status === 'ACTIVE') {
          loadedLocationTagNames.add(tagDoc.id.trim().toLowerCase());
          loadedLocationTagOptions.push({
            id: tagDoc.id,
            name: tag.name,
            description: tag.description,
          });
        }
      });

      setValidLocationTagNames(loadedLocationTagNames);
      setLocationTagOptions(
          loadedLocationTagOptions.sort((tagA, tagB) => tagA.name.localeCompare(tagB.name))
      );
    })();
  }, []);

  const processLocations = useCallback((locations: Array<Location>, currentLocationsMap: Map<string, Location>, currentVisits: Map<string, Visit>, shouldResetPagination: boolean = true, overrideSortType?: string, overrideFilter?: string, overrideQueryString?: string, overrideLocationTagId?: string) => {
    const newLocationsMap = new Map(currentLocationsMap);
    
    locations.forEach((location) => {
      // Set if location is visited
      if(currentUser && currentVisits) {
        let visitCheck = currentVisits.get(location.id);
        location.visited = !!(visitCheck && visitCheck.visited);
      }

      // Set the updated location properties
      let updatedLocation = currentLocationsMap.get(location.id);
      if(updatedLocation) {
        location.visitCount = updatedLocation.visitCount;
        location.ratingCountLow = updatedLocation.ratingCountLow;
        location.ratingCountHigh = updatedLocation.ratingCountHigh;
        location.likeCount = updatedLocation.likeCount;
        location.tags = updatedLocation.tags;
      }

      newLocationsMap.set(location.id, location);
    });

    setLocationsMap(newLocationsMap);

    const activeQueryString = overrideQueryString !== undefined ? overrideQueryString : queryString;
    const processedQueryString = activeQueryString.toLowerCase().replaceAll(" ", "");
    const queryStringMax = processedQueryString.substring(0, processedQueryString.length-1) + 
                          String.fromCharCode(processedQueryString.charCodeAt(processedQueryString.length-1) + 1);

    const activeSortType = overrideSortType || sortType;
    const activeFilter = overrideFilter !== undefined ? overrideFilter : filter;
    const activeLocationTagId = overrideLocationTagId !== undefined ? overrideLocationTagId : selectedLocationTagId;

    let sortedLocations = locations
        .filter((a: Location) => {
          if(!activeFilter) return true;
          switch(activeFilter) {
            case 'temp': return a.pubIsTemporaryClosed;
            case 'closed': return a.pubIsClosed;
            case 'airport': return a.airport;
            case 'hotel': return a.hotel;
            case 'holidaypark': return a.holidayPark;
            case 'university': return a.isUniversity;
            case 'trainstation': return a.trainStation;
            case 'open': return (!a.pubIsClosed && !a.pubIsTemporaryClosed);
            default: return true;
          }
        })
        .filter((a: Location) => {
          if(!activeLocationTagId) return true;

          return a.tags?.some((tagId) => (
              tagId.trim().toLowerCase() === activeLocationTagId.trim().toLowerCase()
          )) || false;
        })
        .filter((a: Location) => {
          if(!processedQueryString) return true;

          const nameToCheck = (a.name || '').toLowerCase().replaceAll(" ", "");
          const cityToCheck = (a.city || '').toLowerCase().replaceAll(" ", "");
          const countyToCheck = (a.county || '').toLowerCase().replaceAll(" ", "");
          const countryToCheck = (a.country || '').toLowerCase().replaceAll(" ", "");

          return (nameToCheck >= processedQueryString && nameToCheck < queryStringMax) ||
                 (cityToCheck >= processedQueryString && cityToCheck < queryStringMax) ||
                 (countyToCheck >= processedQueryString && countyToCheck < queryStringMax) ||
                 (countryToCheck >= processedQueryString && countryToCheck < queryStringMax);
        })
        .sort((a: Location, b: Location) => {
          if(activeSortType === 'visits') {
            return b.visitCount - a.visitCount;
          } else if(activeSortType === 'sessions') {
            return b.sessionUniqueCount - a.sessionUniqueCount;
          } else if (activeSortType === 'likes') {
            return (b.likeCount || 0) - (a.likeCount || 0);
          } else if (activeSortType === 'age') {
            return getTimeOpenInDays(b) - getTimeOpenInDays(a);
          }
          return a.name.localeCompare(b.name);
        });

    setFilteredLocations(sortedLocations);
    setLocationCount(sortedLocations.length);
    
    if (shouldResetPagination) {
      resetPagination();
    }
  }, [currentUser, filter, queryString, selectedLocationTagId, sortType, resetPagination]);

  useEffect(() => {
    if (!currentUser.id || hasInitializedRef.current) return;

    const loadAllData = async () => {
      let newOpenCount = 0;
      const newLocationsMap = new Map<string, Location>();
      
      propLocations.forEach((location) => {
        if(!location.pubIsClosed) {
          newOpenCount++;
        }
        newLocationsMap.set(location.id, location as Location);
      });
      
      setLocationsMap(newLocationsMap);
      setLocationCount(propLocations.length);
      setOpenCount(newOpenCount);

      // Load visits
      const newVisitsMap = new Map<string, Visit>();
      const visitsOnceCollection = query(collection(db, 'visits'), where('userId', '==', currentUser.id));
      const visitsCollectionSnapshot = await getDocs(visitsOnceCollection);
      visitsCollectionSnapshot.forEach((doc) => {
        let tempVisit: Visit = doc.data() as Visit;
        newVisitsMap.set(tempVisit.locationId, tempVisit);
      });

      // Load a visit for every pub if one doesn't already exist
      propLocations.forEach((location) => {
        if(!newVisitsMap.get(location.id)) {
          newVisitsMap.set(location.id, {
            userId: currentUser.id,
            locationId: location.id,
            visited: false,
            lastModified: new Date()
          } as Visit);
        }
      });

      setVisitsMap(newVisitsMap);
      setLoading(false);
      processLocations(propLocations, newLocationsMap, newVisitsMap);
      hasInitializedRef.current = true;
    };

    loadAllData();
  }, [currentUser.id, propLocations, processLocations]);

  // Handle search parameter from URL
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (hasInitializedRef.current && !loading) {
      if (searchParam) {
        setQueryString(searchParam);
        // Scroll to top when navigating from country link
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (prevSearchParamRef.current !== null && searchParam === null) {
        // URL param was explicitly removed (was there, now it's not), clear queryString
        setQueryString('');
      }
    }
    prevSearchParamRef.current = searchParam;
  }, [searchParams, loading]);

  // Trigger search when queryString changes (including from URL param)
  useEffect(() => {
    if (hasInitializedRef.current && !loading && queryString) {
      processLocations(propLocations, locationsMap, visitsMap, true);
    }
  }, [queryString]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUpdatedLocations = async () => {
    const locationsCollection = query(collection(db, 'locations'), where("lastModified", ">=", locationModifiedDate));
    const locationsCollectionSnapshot = await getDocs(locationsCollection);
    
    if (locationsCollectionSnapshot.empty) {
      return; // No updates, don't change anything
    }
    
    const newLocationsMap = new Map(locationsMap);
    let hasUpdates = false;
    let updateCount = 0;
    locationsCollectionSnapshot.forEach((doc) => {
      let visitToChange = visitsMap.get(doc.id);
      if(!visitToChange) {
        return;
      }
      let locationToUpdate = doc.data();
      locationToUpdate.id = doc.id;
      locationToUpdate.visited = visitToChange.visited;
      locationToUpdate.likeCount = doc.data().likeCount;
      newLocationsMap.set(doc.id, locationToUpdate as Location);
      hasUpdates = true;
      updateCount++;
    });
    
    if(hasUpdates) {
      setLocationsMap(newLocationsMap);
      setLocationModifiedDate(new Date());
      
      // Update filtered locations without resetting pagination
      const updatedFilteredLocations = filteredLocations.map(loc => {
        const updatedLoc = newLocationsMap.get(loc.id);
        return updatedLoc ? { ...updatedLoc } : loc;
      });
      setFilteredLocations(updatedFilteredLocations);
    }
  };

  const loadUpdatedVisits = async () => {
    if(!currentUser.id) {
      return;
    }
    const visitsCollection = query(
      collection(db, 'visits'), 
      where('userId', '==', currentUser.id), 
      where("lastModified", ">", visitModifiedDate)
    );
    
    const visitsCollectionSnapshot = await getDocs(visitsCollection);
    
    if (visitsCollectionSnapshot.empty) {
      return; // No updates, don't change anything
    }
    
    const newVisitsMap = new Map(visitsMap);
    let hasUpdates = false;
    let visitCount = 0;
    visitsCollectionSnapshot.forEach((doc) => {
      let tempVisit: Visit = doc.data() as Visit;
      newVisitsMap.set(tempVisit.locationId, tempVisit);
      hasUpdates = true;
      visitCount++;
    });
    
    if(hasUpdates) {
      setVisitsMap(newVisitsMap);
      setVisitModifiedDate(new Date());
      
      // Update filtered locations without resetting pagination  
      const updatedFilteredLocations = filteredLocations.map(loc => {
        const visit = newVisitsMap.get(loc.id);
        if (visit) {
          return { ...loc, visited: visit.visited };
        }
        return loc;
      });
      setFilteredLocations(updatedFilteredLocations);
    }
  };





  const toggleVisited = (locationId: string) => {
    const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
    toggleVisitedFunction({locationId}).then((response) => {
      const result = response.data as { status: number; code: string };
      if (result.status === 403) {
        alert(result.code);
        return;
      }
      loadUpdatedVisits();
      loadUpdatedLocations();
    }).catch((error) => {
      console.error('Error toggling visited:', error);
      alert('Failed to update visit status');
    });

    const newVisitsMap = new Map(visitsMap);
    let visitToChange = newVisitsMap.get(locationId);
    if(!visitToChange) return;

    const wasVisited = visitToChange.visited;
    visitToChange.visited = !wasVisited;
    newVisitsMap.set(locationId, visitToChange);
    setVisitsMap(newVisitsMap);
    updateLocationsMap(locationId, wasVisited);
  };

  const updateLocationsMap = useCallback((locationId: string, wasVisited: boolean) => {
    const newLocationsMap = new Map(locationsMap);
    let locationToChange = newLocationsMap.get(locationId);
    if (!locationToChange) return;

    if (!locationToChange.visitCount && locationToChange.visitCount !== 0) {
      locationToChange.visitCount = 0;
    } else if (wasVisited) {
      locationToChange.visitCount--;
      locationToChange.visited = false;
    } else {
      locationToChange.visitCount++;
      locationToChange.visited = true;
    }
    
    const newVisitCount = locationToChange.visitCount;
    newLocationsMap.set(locationId, locationToChange);
    setLocationsMap(newLocationsMap);
    
    // Update the filtered locations array in place without re-sorting
    const updatedFilteredLocations = filteredLocations.map(loc => {
      if (loc.id === locationId) {
        return { ...loc, visited: !wasVisited, visitCount: newVisitCount };
      }
      return loc;
    });
    setFilteredLocations(updatedFilteredLocations);
  }, [locationsMap, filteredLocations]);



  const handleSort = (newSortType: string) => {
    setSortType(newSortType);
    processLocations(propLocations, locationsMap, visitsMap, true, newSortType);
  };

  const handleFilter = (newFilter: string) => {
    setFilter(newFilter);
    processLocations(propLocations, locationsMap, visitsMap, true, undefined, newFilter);
  };

  const handleLocationTagFilter = (locationTagId: string) => {
    const newLocationTagId = selectedLocationTagId === locationTagId ? '' : locationTagId;
    setSelectedLocationTagId(newLocationTagId);
    processLocations(propLocations, locationsMap, visitsMap, true, undefined, undefined, undefined, newLocationTagId);
  };

  const handleLocationTagsToggle = () => {
    const nextLocationTagsExpanded = !locationTagsExpanded;
    setLocationTagsExpanded(nextLocationTagsExpanded);

    if (!nextLocationTagsExpanded && selectedLocationTagId) {
      setSelectedLocationTagId('');
      processLocations(propLocations, locationsMap, visitsMap, true, undefined, undefined, undefined, '');
    }
  };

  const handleClear = () => {
    setQueryString('');
    setFilter('');
    setSelectedLocationTagId('');
    // Clear the URL search parameter
    navigate('/pubs', { replace: true });
    processLocations(propLocations, locationsMap, visitsMap, true, undefined, '', '', '');
  };

  const handleSearch = (newQueryString: string) => {
    setQueryString(newQueryString);
    processLocations(propLocations, locationsMap, visitsMap, true, undefined, undefined, newQueryString);
  };

  const locateNearestPub = (searchType: NearestPubSearchType) => {
    setFindingNearest(true);
    setNearestPubSearchType(searchType);
    setSelectedNearestPubSearchType(searchType);
    setNearestPubMapCoordinates(undefined);
    setNearestPubMapLocation(undefined);
    navigator.geolocation.getCurrentPosition(
      async currentLocation => {
        const userCoordinates = {
          lat: currentLocation.coords.latitude,
          lng: currentLocation.coords.longitude
        };

        try {
          const findNearestLocation = httpsCallable(functions, 'findNearestLocation');
          const result = await findNearestLocation({
            searchType,
            coords: userCoordinates
          });
          
          if(result.data) {
            const nearestPub = result.data as NearestPub;
            const nearestLocation = locationsMap.get(nearestPub.locationId) || propLocations.find((location) => location.id === nearestPub.locationId);
            setNearestPubId(nearestPub.locationId);
            setNearestPubName(nearestPub.locationName);
            setNearestPubFeedback(formatKilometres(nearestPub.distance));
            setNearestPubMapCoordinates(userCoordinates);
            setNearestPubMapLocation(nearestLocation);
          } else {
            setNearestPubId('');
            setNearestPubName('');
            setNearestPubFeedback('');
            setNearestPubMapCoordinates(undefined);
            setNearestPubMapLocation(undefined);
          }
        } catch (err) {
          console.error(err);
          setNearestPubMapCoordinates(undefined);
          setNearestPubMapLocation(undefined);
        } finally {
          setFindingNearest(false);
        }
      },
      err => {
        alert(
          "Unable to get your location. Make sure you've enabled GPS! " +
          err.message
        );
        setFindingNearest(false);
        setNearestPubMapCoordinates(undefined);
        setNearestPubMapLocation(undefined);
      },
      {
        maximumAge: 1000,
        enableHighAccuracy: true
      }
    );
  };

  return (
    <>
      <PageTracking />
      <AuthCheck />

      {(currentUser && !currentUser.socialDisabled) && (
        <Card>
          <div className={"d-inline-block p-2"}>
            <div className={"d-inline-block"} style={{float: "right"}}>
              <div className={"d-inline-block mr-1"}>
                <small>
                  <Button className={"btn-sm"} onClick={() => setNearestPubExpanded(!nearestPubExpanded)}>
                    <FontAwesomeIcon icon={nearestPubExpanded ? faChevronUp : faChevronDown} />
                  </Button>
                </small>
              </div>
            </div>
            <div className={"d-inline-block mt-1 ml-1 mr-2"} style={{float: "right"}}>
              {nearestPubFeedback === "" && <span>Find your nearest pub</span>}
              {nearestPubFeedback !== "" && (
                <div className={"d-inline-block"}>
                  <LocationTag 
                    name={nearestPubName} 
                    id={nearestPubId} 
                    session={undefined} 
                    displayVerificationTag={false} 
                    linksToTab={"comments"}
                  />
                  <span className={"mr-1"}><b>{nearestPubFeedback}</b></span>
                </div>
              )}
            </div>
          </div>
          {nearestPubExpanded && (
            <Card.Footer className={"p-2"}>
              <div className={"d-flex justify-content-end flex-wrap"}>
                <Button className={`btn-sm mr-2 mb-0 ${selectedNearestPubSearchType === 'all' ? 'active' : ''}`} onClick={() => locateNearestPub('all')} disabled={findingNearest}>
                  {findingNearest && nearestPubSearchType === 'all' && <LoadingTag/>}
                  {(!findingNearest || nearestPubSearchType !== 'all') && <CrosshairsTag/>}
                  <span className={"ml-1"}>All</span>
                </Button>
                <Button className={`btn-sm mr-2 mb-0 ${selectedNearestPubSearchType === 'notVisited' ? 'active' : ''}`} onClick={() => locateNearestPub('notVisited')} disabled={findingNearest}>
                  {findingNearest && nearestPubSearchType === 'notVisited' && <LoadingTag/>}
                  {(!findingNearest || nearestPubSearchType !== 'notVisited') && <FontAwesomeIcon icon={faCheckCircle} />}
                  <span className={"ml-1"}>Not visited</span>
                </Button>
                <Button className={`btn-sm mb-0 ${selectedNearestPubSearchType === 'notCheckedIn' ? 'active' : ''}`} onClick={() => locateNearestPub('notCheckedIn')} disabled={findingNearest}>
                  {findingNearest && nearestPubSearchType === 'notCheckedIn' && <LoadingTag/>}
                  {(!findingNearest || nearestPubSearchType !== 'notCheckedIn') && <FontAwesomeIcon icon={faLocationDot} />}
                  <span className={"ml-1"}>Not checked in</span>
                </Button>
              </div>
              <NearestPubMap
                visible={!!nearestPubMapCoordinates && !!nearestPubMapLocation}
                userCoordinates={nearestPubMapCoordinates}
                location={nearestPubMapLocation}
              />
            </Card.Footer>
          )}
        </Card>
      )}

      <Card className="my-3 location-search-card">
        <Card.Header style={{ padding: '0.75rem' }}>
          {/* Search Bar Row */}
          <Row className="mb-2" style={{marginLeft: "-1px", marginRight: "-1px"}}>
            <Col className="px-0 d-flex">
              <InputGroup className="mr-2">
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  name="location-search"
                  className="tag-input"
                  autoComplete="off"
                  value={queryString}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>
              <Button className={`btn-sm mr-2 ${locationTagsExpanded ? 'active' : ''}`} onClick={handleLocationTagsToggle} aria-label="Pub tags" title="Pub tags">
                <FontAwesomeIcon className={selectedLocationTagId ? 'icon icon-bonus fa-lg mt-1' : 'icon fa-lg mt-1'} icon={faTags} />
              </Button>
              <Button className={"btn-danger btn-sm"} onClick={handleClear} aria-label="Clear search" title="Clear search">
                <FontAwesomeIcon className={'icon fa-lg mt-1'} icon={faDeleteLeft} />
              </Button>
            </Col>
          </Row>

          {/* Filter Buttons Row */}
          <Row style={{marginLeft: "-1px", marginRight: "-1px"}}>
            <Col className="px-0 d-flex justify-content-end">
              <Button className={"btn-sm mr-2"} onClick={() => handleFilter("open")}>
                <FontAwesomeIcon className={filter === 'open' ? 'icon icon-open' : 'icon'} icon={faDoorOpen} />
              </Button>
              <Button className={"btn-sm mr-2"} onClick={() => handleFilter("temp")}>
                <FontAwesomeIcon className={filter === 'temp' ? 'icon icon-temporary' : 'icon'} icon={faHammer} />
              </Button>
              <Button className={"btn-sm mr-2"} onClick={() => handleFilter("closed")}>
                <FontAwesomeIcon className={filter === 'closed' ? 'icon icon-closed' : 'icon'} icon={faTimes} />
              </Button>
              <Button className={"btn-sm mr-2"} onClick={() => handleFilter("airport")}>
                <FontAwesomeIcon className={filter === 'airport' ? 'icon icon-plane' : 'icon'} icon={faPlane} />
              </Button>
              <Button className={"btn-sm mr-2"} onClick={() => handleFilter("hotel")}>
                <FontAwesomeIcon className={filter === 'hotel' ? 'icon icon-hotel' : 'icon'} icon={faHotel} />
              </Button>
              <Button className={"btn-sm"} onClick={() => handleFilter("holidaypark")}>
                <FontAwesomeIcon className={filter === 'holidaypark' ? 'icon icon-holidaypark' : 'icon'} icon={faUmbrellaBeach} />
              </Button>
              <Button className={"btn-sm ml-2"} onClick={() => handleFilter("university")}>
                <FontAwesomeIcon className={filter === 'university' ? 'icon icon-university' : 'icon'} icon={faGraduationCap} />
              </Button>
              <Button className={"btn-sm ml-2"} onClick={() => handleFilter("trainstation")}>
                <FontAwesomeIcon className={filter === 'trainstation' ? 'icon icon-train' : 'icon'} icon={faTrain} />
              </Button>
            </Col>
          </Row>
          {locationTagsExpanded && (
            <Row className="mt-2" style={{marginLeft: "-1px", marginRight: "-1px"}}>
              <Col className="px-0">
                <div className="location-search-tag-options">
                  {locationTagOptions.map((tag) => (
                    <Button
                      key={tag.id}
                      variant={selectedLocationTagId === tag.id ? "secondary" : "outline-secondary"}
                      className="location-search-tag-button"
                      onClick={() => handleLocationTagFilter(tag.id)}
                      title={tag.description || tag.name}
                    >
                      {tag.name}
                    </Button>
                  ))}
                  {locationTagOptions.length === 0 && (
                    <small className="text-muted">No tags available</small>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Card.Header>
      </Card>

      <Card className="my-3">
        <Card.Header>
          <Row style={{marginLeft: "-22px", marginRight: "-22px"}}>
            {locationCount > 0 && (
              <Col className={"col-auto pr-0"}>
                <div style={{display: "inline-block"}}>
                  <span style={{color: "lightgreen"}}>{openCount}</span>
                  {" / "}
                  <span style={{color: "pink"}}>{propLocations.length}</span>
                </div>
              </Col>
            )}
            <Col className="col-auto ml-auto">
              <div>
                <Button 
                  className={"btn-sm mr-2 cursor-pointer"} 
                  disabled={sortType === 'a-z'} 
                  onClick={() => handleSort("a-z")}
                  aria-label="Sort A-Z"
                  title="Sort A-Z"
                >
                  <FontAwesomeIcon icon={faArrowDownAZ} />
                </Button>
                <Button
                  className={"btn-sm mr-2 cursor-pointer"}
                  disabled={sortType === 'likes'}
                  onClick={() => handleSort("likes")}
                >
                  <FontAwesomeIcon icon={faHeart} />
                </Button>
                <Button 
                  className={"btn-sm mr-2 cursor-pointer"} 
                  disabled={sortType === 'visits'} 
                  onClick={() => handleSort("visits")}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
                <Button 
                  className={"btn-sm mr-2 cursor-pointer"} 
                  disabled={sortType === 'sessions'} 
                  onClick={() => handleSort("sessions")}
                >
                  <FontAwesomeIcon icon={faLocationDot} />
                </Button>
                <Button
                  className={"btn-sm cursor-pointer"}
                  disabled={sortType === 'age'}
                  onClick={() => handleSort("age")}
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </Button>

              </div>
            </Col>
          </Row>
        </Card.Header>
        <ListGroup variant="flush">
          {(filteredLocations.length === 0 && loading) && (
            <div className={'loading'}>Loading...</div>
          )}
          {(filteredLocations.length === 0 && !loading) && (
            <div className={'loading'}>No pubs match the search criteria</div>
          )}
          {getPaginatedItems(filteredLocations).map((location: Location, index: number) => (
            <div key={location.id}>
              <LocationRow
                sortType={sortType}
                visit={visitsMap.get(location.id)}
                location={locationsMap.get(location.id)}
                toggleVisited={toggleVisited}
                position={index + 1}
                admin={currentUser.admin}
                validLocationTagNames={validLocationTagNames}
              />
            </div>
          ))}
        </ListGroup>
        <Card.Footer>
          <PaginationControls
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalItems={locationCount}
            onPageLeft={pageLeft}
            onPageRight={() => pageRight(locationCount)}
          />
        </Card.Footer>
      </Card>

      <Card className={'mb-3'}>
        <Card.Footer>
          <Row>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-open mr-1" icon={faDoorOpen} />{" "}
              Open
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-temporary mr-1" icon={faHammer} />{" "}
              Renovating
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-closed mr-1" icon={faTimes} />{" "}
              Closed
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-plane mr-1" icon={faPlane} />{" "}
              Airport
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-hotel mr-1" icon={faHotel} />{" "}
              Hotel
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-holidaypark mr-1" icon={faUmbrellaBeach} />{" "}
              Holiday Park
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-university mr-1" icon={faGraduationCap} />{" "}
              University
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-train mr-1" icon={faTrain} />{" "}
              Train Station
            </Col>
            <Col xs="6" sm="auto" className="text-center">
              <FontAwesomeIcon className="icon-bonus mr-1" icon={faStar} />{" "}
              Bonus
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </>
  );
};

export default Locations;
